import {primitiveType, bufferType} from "_src/engine/constants";
import { Geometry, GeometryBuffer } from "_src/engine/geometry.js";
import arrayTools from "_src/engine/utility/arrays";

// factory
export default {

  create(type, params = {}) {
    switch (type) {
      case primitiveType.PLANE:
        return createPlane(params);
      case primitiveType.TEXT_PLANE:
        return createTextPlane(params);
      case primitiveType.SPHERE:
        return createSphere(params);
      case primitiveType.AABOX:
        return createAABox(params);
      case primitiveType.SCREEN_QUAD:
        return createScreenTriangle();
      case primitiveType.CIRCLE:
        return createCircle(params);
      case primitiveType.LINE:
        return createLine(params);
      case primitiveType.GRID:
        return createGrid(params);
      case primitiveType.LINE_ARRAY:
        return createLines(params);
      case primitiveType.ARROW:
        return createArrow(params);
    }
  }

}

function createPlane( {width = 1, height = 1, xSegments = 1, ySegments = 1} ) {

  const stepX = width  / xSegments;
  const stepY = height / ySegments;

  const seg1X = xSegments + 1;
  const seg1Y = ySegments + 1;

  const width_half  = width / 2;
  const height_half = height / 2;

  const numVertices = seg1Y * seg1X;

  const posNormByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const uvByteLength = 2 * numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posNormByteLength);
  const normBuffer = new ArrayBuffer(posNormByteLength);
  const uvBuffer = new ArrayBuffer(uvByteLength);

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const normalItem = new GeometryBuffer({ buffer : normBuffer, type : bufferType.F32_3 });
  const uvItem = new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_2 });

  const positions = positionsItem.getBuffer();
  const normals = normalItem.getBuffer();
  const uvs = uvItem.getBuffer();

  const indicesData = [];

  for ( let iy = 0; iy < seg1Y; ++iy ) {
    const y = iy * stepY - height_half;
    for ( let ix = 0; ix < seg1X; ++ix ) {

      const offset = iy * seg1X + ix;

      const posNormOffset = 3 * offset;
      const uvOffset = 2 * offset;

      const x = ix * stepX - width_half;

      positions[posNormOffset] = x;
      positions[posNormOffset + 1] = y;
      positions[posNormOffset + 2] = 0;

      normals[posNormOffset] = 0;
      normals[posNormOffset + 1] = 0;
      normals[posNormOffset + 2] = 1;

      uvs[uvOffset] = ix / xSegments;
      uvs[uvOffset + 1] = 1 - (iy / ySegments);

    }

  }

  for ( var iy = 0; iy < ySegments; iy ++ ) {
    for ( var ix = 0; ix < xSegments; ix ++ ) {

      const i1 = ix + seg1X * iy;
      const i2 = ix + seg1X * ( iy + 1 );
      const i3 = ( ix + 1 ) + seg1X * ( iy + 1 );
      const i4 = ( ix + 1 ) + seg1X * iy;

      indicesData.push( i1, i4, i2 );
      indicesData.push( i2, i4, i3 );
    }
  }

  const indices = generateIndBuf(indicesData, positions);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const bbox = [-0.5 * width, -0.5 * height, 0,
                 0.5 * width, 0.5 * height,  0 ];

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_normal" : normalItem,
    "a_uv" : uvItem
  };

  return new Geometry( { layout, bbox } );

}

function getCharDescriptor(char, fontDescriptor) {
  const charCode = char.charCodeAt(0);
  let charDescriptor = fontDescriptor.find(cd => cd["unicode"] === charCode);
  if (!charDescriptor) {
    charDescriptor = fontDescriptor[0];
  }

  return charDescriptor;
}

function createTextPlane( {text, fontDescriptor, textureWidth, xStart, yStart} ) {
  const charsCount = text.length;
  let width = 0;
  for (const char of text) {
    const charDescriptor = getCharDescriptor(char, fontDescriptor);
    const charAdvance = charDescriptor["advance"];

    width += charAdvance;
  }

  if (xStart === undefined) {
    xStart = -width/2;
  }

  if (yStart === undefined) {
    yStart = -0.5;
  }

  const numVertices = charsCount * 4;

  const posNormByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const uvByteLength = 2 * numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posNormByteLength);
  const normBuffer = new ArrayBuffer(posNormByteLength);
  const uvBuffer = new ArrayBuffer(uvByteLength);

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const normalItem = new GeometryBuffer({ buffer : normBuffer, type : bufferType.F32_3 });
  const uvItem = new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_2 });

  const positions = positionsItem.getBuffer();
  const normals = normalItem.getBuffer();
  const uvs = uvItem.getBuffer();
  const indicesData = [];

  let currentX = xStart;
  for (let i=0; i<charsCount; i++) {
    const char = text[i];
    const charDescriptor = getCharDescriptor(char, fontDescriptor);
    const charAdvance = charDescriptor["advance"];
    const planeBounds = charDescriptor["planeBounds"] ? charDescriptor["planeBounds"] : {left: 0, right: 0, bottom: 0, top: 0};
    const atlasBounds = charDescriptor["atlasBounds"] ? charDescriptor["atlasBounds"] : {left: 0, right: 0, bottom: 0, top: 0};

    positions[12*i+0] = currentX + planeBounds["left"];
    positions[12*i+1] = yStart + planeBounds["bottom"];
    positions[12*i+2] = 0;

    positions[12*i+3] = currentX + planeBounds["right"];
    positions[12*i+4] = yStart + planeBounds["bottom"];
    positions[12*i+5] = 0;

    positions[12*i+6] = currentX + planeBounds["right"];
    positions[12*i+7] = yStart + planeBounds["top"];
    positions[12*i+8] = 0;

    positions[12*i+9] = currentX + planeBounds["left"];
    positions[12*i+10] = yStart + + planeBounds["top"];
    positions[12*i+11] = 0;

    normals[12*i+0] = 0;
    normals[12*i+1] = 0;
    normals[12*i+2] = 1;

    normals[12*i+3] = 0;
    normals[12*i+4] = 0;
    normals[12*i+5] = 1;

    normals[12*i+6] = 0;
    normals[12*i+7] = 0;
    normals[12*i+8] = 1;

    normals[12*i+9] = 0;
    normals[12*i+10] = 0;
    normals[12*i+11] = 1;

    uvs[8*i+0] = atlasBounds["left"] / textureWidth;
    uvs[8*i+1] = atlasBounds["bottom"] / textureWidth;

    uvs[8*i+2] = atlasBounds["right"] / textureWidth;
    uvs[8*i+3] = atlasBounds["bottom"] / textureWidth;

    uvs[8*i+4] = atlasBounds["right"] / textureWidth;
    uvs[8*i+5] = atlasBounds["top"] / textureWidth;

    uvs[8*i+6] = atlasBounds["left"] / textureWidth;
    uvs[8*i+7] = atlasBounds["top"] / textureWidth;

    indicesData.push(i*4+0, i*4+1, i*4+3);
    indicesData.push(i*4+3, i*4+1, i*4+2);

    currentX += charAdvance;
  }

  const indices = generateIndBuf(indicesData, positions);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const bbox = [-0.5 * width, -0.5 , 0, 0.5 * width, 0.5,  0 ];

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_normal" : normalItem,
    "a_uv" : uvItem
  };

  return new Geometry( { layout, bbox } );

}

function createSphere({ radius = 1, widthSegments = 12, heightSegments = 6 }) {

  const phiStart = 0;
  const phiLength = Math.PI * 2;
  const thetaStart = 0;
  const thetaLength = Math.PI;

  const thetaEnd = thetaStart + thetaLength;

  let index = 0;
  const grid = [];

  // buffers

  const numVertices = (widthSegments + 1) * (heightSegments + 1);
  const numIndices = 6 * (widthSegments) * (heightSegments - 1);

  const posNormByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posNormByteLength);
  const normBuffer = new ArrayBuffer(posNormByteLength);

  const indices = arrayTools.createUintArrayByValueLength(numVertices - 1,
                                                          numIndices);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const normalItem = new GeometryBuffer({ buffer : normBuffer, type : bufferType.F32_3 });

  const positions = positionsItem.getBuffer();
  const normals = normalItem.getBuffer();

  const minmax = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];

  for (let iy = 0; iy <= heightSegments; ++iy) {

    const verticesRow = [];
    const v = iy / heightSegments;

    for (let ix = 0; ix <= widthSegments; ++ix) {

      const u = ix / widthSegments;

      // vertex
      let x = -radius * Math.cos(phiStart + u * phiLength) * Math.sin(thetaStart + v * thetaLength);
      let y = -radius * Math.sin(phiStart + u * phiLength) * Math.sin(thetaStart + v * thetaLength);
      let z = radius * Math.cos(thetaStart + v * thetaLength);

      const ind = 3 * index;

      positions[ind] = x;
      positions[ind + 1] = y;
      positions[ind + 2] = z;

      // Bounding box
      minmax[0] = Math.min(minmax[0], x);
      minmax[1] = Math.min(minmax[1], y);
      minmax[2] = Math.min(minmax[2], z);
      minmax[3] = Math.max(minmax[3], x);
      minmax[4] = Math.max(minmax[4], y);
      minmax[5] = Math.max(minmax[5], z);

      // normal
      let len = x*x + y*y + z*z;
      len = 1 / Math.sqrt(len);

      normals[ind]     = x * len;
      normals[ind + 1] = y * len;
      normals[ind + 2] = z * len;

      verticesRow.push(index++);

    }

    grid.push(verticesRow);

  }

  // indices

  let idx = 0;
  for (let iy = 0; iy < heightSegments; ++iy) {

    for (let ix = 0; ix < widthSegments; ++ix) {

      const a = grid[iy][ix + 1];
      const b = grid[iy][ix];
      const c = grid[iy + 1][ix];
      const d = grid[iy + 1][ix + 1];

      if ( iy !== 0 || thetaStart > 0 ) {
        indices[idx++] = a;
        indices[idx++] = b;
        indices[idx++] = d;
      }

      if (iy !== heightSegments - 1 || thetaEnd < Math.PI) {
        indices[idx++] = b;
        indices[idx++] = c;
        indices[idx++] = d;
      }

    }

  }

  const bbox = minmax;

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_normal" : normalItem
  };

  return new Geometry( { layout, bbox } );

}

function createAABox({ halfX = 1, halfY = 1, halfZ = 1 }) {

  const numVertices = 64 + 36;
  const numIndices = 6 * numVertices / 4;

  const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const uvByteLength = numVertices * Float32Array.BYTES_PER_ELEMENT;
  const indByteLength = numVertices * Uint8Array.BYTES_PER_ELEMENT;

  const indices = arrayTools.createUintArrayByValueLength(numVertices - 1,
                                                          numIndices);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const posBuffer = new ArrayBuffer(posByteLength);
  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positions = positionsItem.getBuffer();

  const uvBuffer = new ArrayBuffer(uvByteLength);
  const uvItem = new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_1 });
  const uvs = uvItem.getBuffer();

  const posNextBuffer = new ArrayBuffer(posByteLength);
  const positionsNextItem = new GeometryBuffer({ buffer : posNextBuffer, type : bufferType.F32_3 });
  const positionsNext = positionsNextItem.getBuffer();

  const vertIdBuffer = new ArrayBuffer(indByteLength);
  const vertexIdItem = new GeometryBuffer({ buffer : vertIdBuffer, type : bufferType.UI8_1 });
  const vertexIds = vertexIdItem.getBuffer();

  const x = halfX;
  const y = halfY;
  const z = halfZ;

  const positionsData = [x,-y,z, x,-y,-z,  x,-y,-z, -x,-y,-z, -x,-y,-z, -x,-y,z, -x,-y,z, x,-y,z,
                         x,y,z, x,y,-z,  x,y,-z, -x,y,-z, -x,y,-z, -x,y,z, -x,y,z, x,y,z];

  for (let i = 0; i < 16; i += 2) {

    const ind = 3 * i;
    const indNext = 3 * (i + 1);

    const x0 = positionsData[ind];
    const y0 = positionsData[ind + 1];
    const z0 = positionsData[ind + 2];

    const x1 = positionsData[indNext];
    const y1 = positionsData[indNext + 1];
    const z1 = positionsData[indNext + 2];

    const posOffset = 0.5 * 3 * i * 4;
    const uvOffset = 0.5 * i * 4;
    const indOffset = 0.5 * 6 * i;

    addLineToBuffer(x0, y0, z0, x1, y1, z1, 0, 0, positions, positionsNext, vertexIds, uvs, null, indices,
                    posOffset, indOffset, uvOffset);

  }

  const posOffset = 12 * 8;
  const indOffset = 6 * 8;
  const uvOffset = 4 * 8;

  addLineToBuffer(x,-y,z,x,y,z,0,0, positions, positionsNext, vertexIds, uvs, null, indices,
                  posOffset, indOffset, uvOffset);
  addLineToBuffer(x,-y,-z,x,y,-z,0,0, positions, positionsNext, vertexIds, uvs, null, indices,
                  posOffset + 12, indOffset + 6, uvOffset + 4);
  addLineToBuffer(-x,-y,-z,-x,y,-z,0,0,positions, positionsNext, vertexIds, uvs, null, indices,
                  posOffset + 24, indOffset + 12, uvOffset + 8);
  addLineToBuffer(-x,-y,z,-x,y,z,0,0, positions, positionsNext, vertexIds, uvs, null, indices,
                  posOffset + 36, indOffset + 18, uvOffset + 12);

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_position_next" : positionsNextItem,
    "a_vertex_id" : vertexIdItem,
    "a_uv" : uvItem
  };

  return new Geometry( { layout} );

}

function createCircle({ numPoints = 5, radius = 1}) {

  const positionsData = [];
  const indicesData = [];

  let x = radius;
  let y = 0.0;
  let z = 0.0;

  const deltaAngle = 2.0 * Math.PI / numPoints;

  const cosa = Math.cos(deltaAngle);
  const sina = Math.sin(deltaAngle);

  positionsData.push(x, y, z);

  for (let i = 1; i < numPoints; ++i) {

    const X = x;
    const Z = z;

    x = cosa * X - sina * Z;
    z = sina * X + cosa * Z;

    positionsData.push(x, y, z);
    indicesData.push(i - 1, i);

  }

  indicesData.push(numPoints - 1, 0);

  const posByteLength = 3 * numPoints * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posByteLength);

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positions = positionsItem.getBuffer();

  positions.set(positionsData);
  const indices = generateIndBuf(indicesData, positionsData);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem
  };

  return new Geometry( { layout} );

}

function createLine({ x0 = 0, y0 = 0, z0 = 0, x1 = 0, y1 = 0, z1 = 0 }) {

  const numVertices = 4;
  const numIndices = 6;

  const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const uvByteLength = numVertices * Float32Array.BYTES_PER_ELEMENT;
  const indByteLength = numVertices * Uint8Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posByteLength);
  const nextPosBuffer = new ArrayBuffer(posByteLength);
  const vertIdBuffer = new ArrayBuffer(indByteLength);
  const uvBuffer = new ArrayBuffer(uvByteLength);

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positionsNextItem = new GeometryBuffer({ buffer : nextPosBuffer, type : bufferType.F32_3 });
  const vertexIdItem = new GeometryBuffer({ buffer : vertIdBuffer, type : bufferType.UI8_1 });
  const uvItem = new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_1 });

  const positions = positionsItem.getBuffer();
  const positionsNext = positionsNextItem.getBuffer();
  const vertexIds = vertexIdItem.getBuffer();
  const uvs = uvItem.getBuffer();

  const indices = arrayTools.createUintArrayByValueLength(numVertices - 1,
                                                          numIndices);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  addLineToBuffer(x0,y0,z0, x1,y1,z1, 0,0, positions, positionsNext, vertexIds, uvs, null, indices,
                    0, 0, 0);

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_position_next" : positionsNextItem,
    "a_vertex_id" : vertexIdItem,
    "a_uv" : uvItem
  };

  return new Geometry( { layout} );

}

function createGrid({ xLines = 5, yLines = 5, xStep = 1, yStep = 1 }) {

  const numIndices = 2 * 6 * xLines + 2 * 6 * (yLines + 1);
  const numVertices = 2 * 4 * xLines + 2 * 4 * (yLines + 1);

  const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const indByteLength = numVertices * Uint8Array.BYTES_PER_ELEMENT;
  const uvByteLength = numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posByteLength);
  const nextPosBuffer = new ArrayBuffer(posByteLength);
  const vertIdBuffer = new ArrayBuffer(indByteLength);
  const uvBuffer = new ArrayBuffer(uvByteLength);

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positionsNextItem = new GeometryBuffer({ buffer : nextPosBuffer, type : bufferType.F32_3 });
  const vertexIdItem = new GeometryBuffer({ buffer : vertIdBuffer, type : bufferType.UI8_1 });
  const uvItem = new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_1 });

  const positions = positionsItem.getBuffer();
  const positionsNext = positionsNextItem.getBuffer();
  const vertexIds = vertexIdItem.getBuffer();
  const uvs = uvItem.getBuffer();

  const indices = arrayTools.createUintArrayByValueLength(numVertices - 1,
                                                          numIndices);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const halfWidth = -yLines * yStep;
  const halfHeight = -xLines * xStep;

  let posOffset = 0;
  let indOffset = 0;
  let uvOffset = 0;

  for (let i = -xLines; i <= xLines; ++i) {

    const x0 = i * xStep;

    const y0 = halfWidth;
    const y1 = -halfWidth;

    addLineToBuffer(x0, y0, 0, x0, y1, 0, 0, 0, positions, positionsNext, vertexIds, uvs, null, indices,
                    posOffset, indOffset, uvOffset);

    indOffset += 6;
    posOffset += 12;
    uvOffset += 4;

  }

  for (let i = -yLines; i <= yLines; ++i) {

    const y0 = i * yStep;

    const x0 = halfHeight;
    const x1 = -halfHeight;

    addLineToBuffer(x0, y0, 0, x1, y0, 0, 0, 0, positions, positionsNext, vertexIds, uvs, null, indices,
                    posOffset, indOffset, uvOffset);

    indOffset += 6;
    posOffset += 12;
    uvOffset += 4;

  }

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_position_next" : positionsNextItem,
    "a_vertex_id" : vertexIdItem,
    "a_uv" : uvItem
  };

  return new Geometry( { layout } );

}

function createLines({ points, primitives, attribute, aspect = 1 }) {

  const numLines = primitives.length / 2;
  const numIndices = 6 * numLines;
  const numVertices = 4 * numLines;

  const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const indByteLength = numVertices * Uint8Array.BYTES_PER_ELEMENT;
  const uvByteLength = numVertices * Float32Array.BYTES_PER_ELEMENT;
  const attrByteLength = numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posByteLength);
  const nextPosBuffer = new ArrayBuffer(posByteLength);
  const vertIdBuffer = new ArrayBuffer(indByteLength);
  const uvBuffer = !attribute ? new ArrayBuffer(uvByteLength) : null;
  const attrBuffer = attribute ? new ArrayBuffer(attrByteLength): null;

  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positionsNextItem = new GeometryBuffer({ buffer : nextPosBuffer, type : bufferType.F32_3 });
  const vertexIdItem = new GeometryBuffer({ buffer : vertIdBuffer, type : bufferType.UI8_1 });
  const uvItem = uvBuffer ? new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_1 }) : null;
  const attrItem = attrBuffer ? new GeometryBuffer({ buffer : attrBuffer, type : bufferType.F32_1 }) : null;

  const posTypedBuffer = positionsItem.getBuffer();
  const nextPosTypedBuffer = positionsNextItem.getBuffer();
  const vertIdTypedBuffer = vertexIdItem.getBuffer();
  const uvTypedBuffer = uvItem ? uvItem.getBuffer() : null;
  const attrTypedBuffer = attrItem ? attrItem.getBuffer() : null;

  const indices = arrayTools.createUintArrayByValueLength(numVertices - 1,
                                                          numIndices);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  let posOffset = 0;
  let indOffset = 0;
  let uvOffset = 0;
  let uvLength = 0.0;

  for (let i = 0; i < primitives.length; i += 2) {

    const v0 = primitives[i];
    const v1 = primitives[i + 1];

    const x0 = points[v0 * 3];
    const y0 = points[v0 * 3 + 1];
    const z0 = points[v0 * 3 + 2];

    const x1 = points[v1 * 3];
    const y1 = points[v1 * 3 + 1];
    const z1 = points[v1 * 3 + 2];

    let [a0, a1] = [0,0];
    if (attribute) {
      a0 = attribute[v0];
      a1 = attribute[v1];
    }

    addLineToBuffer(x0, y0, z0, x1, y1, z1, a0, a1, posTypedBuffer, nextPosTypedBuffer, vertIdTypedBuffer, uvTypedBuffer, attrTypedBuffer, indices,
                    posOffset, indOffset, uvOffset, uvLength, aspect);

    indOffset += 6;
    posOffset += 12;
    uvOffset += 4;

    uvLength += Math.sqrt((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0) / aspect / aspect);

  }

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_position_next" : positionsNextItem,
    "a_vertex_id" : vertexIdItem,
  };
  if (uvItem) layout["a_uv"] = uvItem;
  if (attrItem) layout["a_custom"] = attrItem;

  return new Geometry( { layout } );

}

function createScreenTriangle() {

  const numVertices = 4;
  const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;
  const uvByteLength = 2 * numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posByteLength);
  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positions = positionsItem.getBuffer();

  const uvBuffer = new ArrayBuffer(uvByteLength);
  const uvItem = new GeometryBuffer({ buffer : uvBuffer, type : bufferType.F32_2 });
  const uv = uvItem.getBuffer();

  positions.set([-1,-1,0, 1,-1,0, -1,1,0, 1,1,0]);
  uv.set([-1,-1, 1,-1, -1,1, 1,1]);

  const indices = generateIndBuf([0,1,2, 1,3,2], positions);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
    "a_uv" : uvItem,
  };

  return new Geometry( { layout} );
}

function generateIndBuf(indicesData, positions) {
  const maxValue = positions.length / 3 - 1;
  return arrayTools.createUintArrayByValueData(maxValue, indicesData);
}

function addLineToBuffer(x0, y0, z0, x1, y1, z1, a0, a1, positions, positionsNext, vertexIds, uvs, attribute,
    indices, posOffset, indOffset, uvOffset, uvStartValue = 0, aspect = 1) {

  const vertOffset = 4 * indOffset / 6;

  indices[indOffset] = 0 + vertOffset;
  indices[indOffset + 1] = 1 + vertOffset;
  indices[indOffset + 2] = 2 + vertOffset;

  indices[indOffset + 3] = 0 + vertOffset;
  indices[indOffset + 4] = 3 + vertOffset;
  indices[indOffset + 5] = 1 + vertOffset;

  const uvValue = Math.sqrt((x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0) / aspect / aspect + (z1 - z0) * (z1 - z0));

  // 0

  let offset = posOffset;
  let vertexIdOffset = posOffset / 3;

  positions[offset] = x0;
  positions[offset + 1] = y0;
  positions[offset + 2] = z0;

  if (uvs) uvs[uvOffset] = uvStartValue;
  if (attribute) attribute[offset / 3] = a0;

  positionsNext[offset] = x1;
  positionsNext[offset + 1] = y1;
  positionsNext[offset + 2] = z1;

  vertexIds[vertexIdOffset++] = 0;

  // 1
  offset += 3;

  positions[offset] = x1;
  positions[offset + 1] = y1;
  positions[offset + 2] = z1;

  if (uvs) uvs[uvOffset + 1] = uvStartValue + uvValue;
  if (attribute) attribute[offset / 3] = a1;

  positionsNext[offset] = x0;
  positionsNext[offset + 1] = y0;
  positionsNext[offset + 2] = z0;

  vertexIds[vertexIdOffset++] = 1;

  // 2

  offset += 3;

  positions[offset] = x0;
  positions[offset + 1] = y0;
  positions[offset + 2] = z0;

  if (uvs) uvs[uvOffset + 2] = uvStartValue;
  if (attribute) attribute[offset / 3] = a0;

  positionsNext[offset] = x1;
  positionsNext[offset + 1] = y1;
  positionsNext[offset + 2] = z1;

  vertexIds[vertexIdOffset++] = 2;

  // 3

  offset += 3;

  positions[offset] = x1;
  positions[offset + 1] = y1;
  positions[offset + 2] = z1;

  if (uvs) uvs[uvOffset + 3] = uvStartValue + uvValue;
  if (attribute) attribute[offset / 3] = a1;

  positionsNext[offset] = x0;
  positionsNext[offset + 1] = y0;
  positionsNext[offset + 2] = z0;

  vertexIds[vertexIdOffset++] = 3;

}

function createArrow() {
  const numVertices = 3;
  const posByteLength = 3 * numVertices * Float32Array.BYTES_PER_ELEMENT;

  const posBuffer = new ArrayBuffer(posByteLength);
  const positionsItem = new GeometryBuffer({ buffer : posBuffer, type : bufferType.F32_3 });
  const positions = positionsItem.getBuffer();

  positions.set([0,0,0, -1,4,0, 1,4,0]);

  const indices = generateIndBuf([0,1,2], positions);
  const indicesType = arrayTools.uintArrayType(indices, 3);
  const indicesItem = new GeometryBuffer({ buffer : indices.buffer, type : indicesType });

  const layout = {
    "indices" : indicesItem,
    "a_position" : positionsItem,
  };

  return new Geometry( { layout} );
}
